// SocialMedia.jsx — Unified social media inbox + composer + integration setup.
// Connects to /api/social/* endpoints.
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatErr } from "./lib/api";
import { useAuth } from "./lib/auth";
import { showToast } from "./lib/theme";

const PLATFORMS = [
  { key: "instagram", label: "إنستغرام", color: "#E1306C", icon: "📸" },
  { key: "facebook",  label: "فيسبوك",   color: "#1877F2", icon: "👤" },
  { key: "twitter",   label: "X",        color: "#000",    icon: "𝕏"  },
  { key: "tiktok",    label: "تيك توك",  color: "#000",    icon: "🎵" },
  { key: "snapchat",  label: "سناب",     color: "#FFFC00", icon: "👻" },
];

const PLATFORM_META = Object.fromEntries(PLATFORMS.map((p) => [p.key, p]));
const KIND_LABEL = { dm: "💬 رسالة", comment: "🗨 تعليق", mention: "📣 إشارة", review: "⭐ مراجعة" };
const STATUS_LABEL = { open: "🆕 جديدة", replied: "✅ تم الرد", archived: "📦 مؤرشفة", snoozed: "⏰ مؤجلة" };

function relTime(iso) {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (isNaN(t)) return "—";
  const m = Math.floor((Date.now() - t) / 60000);
  if (m < 1) return "الآن";
  if (m < 60) return `قبل ${m} د`;
  if (m < 1440) return `قبل ${Math.floor(m / 60)} س`;
  const d = Math.floor(m / 1440);
  return `قبل ${d} يوم`;
}

export default function SocialMedia() {
  const { user } = useAuth();
  const [view, setView] = useState("analytics"); // analytics | compose | settings

  return (
    <div data-testid="social-media-page">
      {/* Hero */}
      <div style={{
        marginBottom: 14, padding: "16px 22px",
        background: "linear-gradient(135deg, #6B4E3D 0%, #8B6F58 100%)", color: "#fff",
        borderRadius: 14,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: 1 }}>السوشل ميديا</div>
            <h2 style={{ margin: "2px 0", fontSize: 22, fontWeight: 800 }}>📣 النشر والتحليلات</h2>
            <div style={{ fontSize: 12, opacity: 0.85 }}>
              النشر والتحليلات هنا — الردود على إنستغرام/ماسنجر من الصندوق الموحّد فقط
            </div>
          </div>
        </div>
      </div>

      {/* ج٥ — صندوق السوشل القديم أُوقف: الرد حصراً من الصندوق الموحّد */}
      <div data-testid="social-inbox-sunset-banner" style={{ marginBottom: 14, padding: "12px 16px", borderRadius: 10,
        background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412", fontSize: 13, display: "flex",
        gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span>📨 صندوق وارد السوشل انتقل إلى <b>الصندوق الموحّد</b> — الرسائل والردود على إنستغرام وماسنجر هناك.</span>
        <button type="button" data-testid="social-goto-unified-inbox"
          onClick={() => {
            try { const u = new URL(window.location.href); u.searchParams.set("tab", "comms_hub"); u.searchParams.set("sub", "unified"); window.history.replaceState(null, "", u.toString()); } catch (_e) { /* noop */ }
            window.dispatchEvent(new CustomEvent("tw-navigate", { detail: { tab: "comms_hub" } }));
          }}
          style={{ padding: "6px 14px", borderRadius: 999, border: "none", background: "#9a3412", color: "#fff", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>
          فتح الصندوق الموحّد ←
        </button>
      </div>

      {/* View tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }} data-testid="social-view-tabs">
        {[
          { k: "analytics", l: "📊 التحليلات" },
          { k: "compose",   l: "✍ نشر منشور" },
        ].filter((t) => !t.allowedRoles || t.allowedRoles.includes(user?.role)).map((t) => (
          <button key={t.k} onClick={() => setView(t.k)} data-testid={`social-view-${t.k}`}
                  style={{
                    padding: "8px 16px", fontSize: 13, fontWeight: 700,
                    background: view === t.k ? "var(--brown)" : "var(--beige)",
                    color: view === t.k ? "#fff" : "var(--brown-deep)",
                    border: "none", borderRadius: 8, cursor: "pointer",
                    transition: "all 160ms ease", display: "flex", alignItems: "center", gap: 6,
                  }}>
            {t.l}
            {t.count > 0 && view !== t.k && (
              <span style={{ background: "var(--danger)", color: "#fff", borderRadius: 999, padding: "1px 7px", fontSize: 10, fontWeight: 800 }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {view === "analytics" && (
        <div className="card" data-testid="social-analytics-deferred" style={{ padding: 16, fontSize: 13 }}>
          التحليلات غير مربوطة — مؤجّلة بقرار المالك
        </div>
      )}
      {view === "compose"   && <ComposeView onChange={() => {}} />}
    </div>
  );
}

function ComposeView({ onChange }) {
  const [text, setText] = useState("");
  const [platforms, setPlatforms] = useState(["instagram", "facebook"]);
  const [topic, setTopic] = useState("");
  const [intent, setIntent] = useState("offer");
  const [includeHash, setIncludeHash] = useState(true);
  const [scheduledFor, setScheduledFor] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState([]);

  const loadQueue = useCallback(async () => {
    try { const { data } = await api.get("/social/publish-queue?limit=20"); setQueue(data.items || []); } catch { setQueue([]); }
  }, []);
  useEffect(() => { loadQueue(); }, [loadQueue]);

  const togglePlatform = (k) => setPlatforms((cur) => cur.includes(k) ? cur.filter((p) => p !== k) : [...cur, k]);

  const generate = useCallback(async () => {
    if (!topic.trim()) { setErr("اكتب موضوع المنشور أولاً"); return; }
    setAiBusy(true); setErr("");
    try {
      const { data } = await api.post("/social/ai-generate-post", {
        intent, topic, platforms, include_emoji: true, include_hashtags: includeHash,
      });
      setText(data.text || "");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setAiBusy(false); }
  }, [intent, topic, platforms, includeHash]);

  const queuePost = useCallback(async () => {
    if (!text.trim()) { setErr("اكتب نص المنشور"); return; }
    if (!platforms.length) { setErr("اختر منصة واحدة على الأقل"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/social/publish-queue", { text, platforms, scheduled_for: scheduledFor });
      showToast("✅ المنشور أُضيف لقائمة النشر", "success");
      setText(""); setTopic(""); setScheduledFor("");
      loadQueue(); onChange?.();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }, [text, platforms, scheduledFor, loadQueue, onChange]);

  return (
    <div className="g2" style={{ gap: 14, gridTemplateColumns: "1fr 380px" }}>
      <div className="card" data-testid="social-compose-form">
        <h3 style={{ marginTop: 0, color: "var(--brown-deep)" }}>✍ منشور جديد</h3>

        {/* Step 1: AI helper */}
        <div style={{ background: "var(--beige)", padding: 12, borderRadius: 10, marginBottom: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 8 }}>
            🧠 يولّد Claude منشورًا جاهزًا
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
            {[
              { k: "offer",     l: "🎁 عرض" },
              { k: "menu_item", l: "☕ صنف جديد" },
              { k: "event",     l: "🎉 فعالية" },
              { k: "thanks",    l: "🙏 شكر" },
              { k: "general",   l: "📝 عام" },
            ].map((i) => (
              <button key={i.k} onClick={() => setIntent(i.k)} data-testid={`social-intent-${i.k}`}
                      style={{
                        padding: "5px 12px", fontSize: 11.5, fontWeight: 600, border: "none",
                        background: intent === i.k ? "var(--brown)" : "#fff",
                        color: intent === i.k ? "#fff" : "var(--brown-deep)",
                        borderRadius: 999, cursor: "pointer",
                      }}>{i.l}</button>
            ))}
          </div>
          <input type="text" value={topic} onChange={(e) => setTopic(e.target.value)}
                 data-testid="social-topic-input"
                 placeholder="موضوع المنشور (مثل: خصم 20% على الفلات وايت يوم الجمعة)"
                 style={{ width: "100%", padding: "8px 12px", fontSize: 12.5, borderRadius: 8, border: "1px solid var(--border)", marginBottom: 8 }} />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <label style={{ fontSize: 11, color: "var(--muted)" }}>
              <input type="checkbox" checked={includeHash} onChange={(e) => setIncludeHash(e.target.checked)} data-testid="social-include-hashtags" />
              {" "}أضف هاشتاقات
            </label>
            <button onClick={generate} disabled={aiBusy} data-testid="social-generate-btn"
                    style={{
                      marginInlineStart: "auto", padding: "6px 16px",
                      background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                      color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
                      opacity: aiBusy ? 0.6 : 1,
                    }}>
              {aiBusy ? "⏳ Claude يكتب..." : "✨ ولّد المنشور"}
            </button>
          </div>
        </div>

        {/* Step 2: Edit */}
        <label style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown-deep)" }}>📝 نص المنشور</label>
        <textarea value={text} onChange={(e) => setText(e.target.value)}
                  data-testid="social-text-textarea"
                  rows={7} placeholder="اكتب أو ولّد منشورك هنا..."
                  style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid var(--border)",
                           fontSize: 13.5, fontFamily: "var(--f-n)", lineHeight: 1.8, resize: "vertical", marginTop: 6, marginBottom: 12 }} />

        {/* Step 3: Platforms */}
        <label style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown-deep)" }}>📡 المنصات</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6, marginBottom: 12 }} data-testid="social-platforms-pickers">
          {PLATFORMS.map((p) => {
            const on = platforms.includes(p.key);
            return (
              <button key={p.key} type="button" onClick={() => togglePlatform(p.key)}
                      data-testid={`social-platform-${p.key}`}
                      style={{
                        padding: "8px 14px", fontSize: 12.5, fontWeight: 700, border: `1.5px solid ${on ? p.color : "var(--border)"}`,
                        background: on ? p.color : "#fff", color: on ? "#fff" : "var(--brown-deep)",
                        borderRadius: 999, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                        transition: "all 160ms ease",
                      }}>
                <span>{p.icon}</span> {p.label}
              </button>
            );
          })}
        </div>

        {/* Step 4: Schedule */}
        <label style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown-deep)" }}>⏰ موعد النشر (اتركه فاضي للنشر فوراً)</label>
        <input type="datetime-local" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)}
               data-testid="social-schedule"
               style={{ padding: "8px 12px", fontSize: 12.5, borderRadius: 8, border: "1px solid var(--border)", marginTop: 6, marginBottom: 14 }} />

        {err && <div className="al alr" style={{ marginBottom: 12 }} data-testid="social-compose-error">{err}</div>}

        <button onClick={queuePost} disabled={busy} data-testid="social-queue-btn"
                className="btn btn-primary" style={{ width: "100%", fontSize: 13 }}>
          {busy ? "⏳ جارٍ الإضافة..." : "🚀 أضف لقائمة النشر"}
        </button>
      </div>

      {/* Queue */}
      <div className="card" data-testid="social-queue-list">
        <h4 style={{ marginTop: 0, color: "var(--brown-deep)" }}>📋 قائمة النشر</h4>
        {queue.length === 0 && <div style={{ color: "var(--muted)", fontSize: 12, padding: 8 }}>لا توجد منشورات في القائمة</div>}
        {queue.map((q) => (
          <div key={q.id} data-testid={`queue-item-${q.id}`} style={{
            padding: 10, marginBottom: 8, background: "var(--beige)", borderRadius: 8,
            borderInlineStart: `3px solid ${q.status === "published" ? "var(--success)" : q.status === "failed" ? "var(--danger)" : "var(--warn)"}`,
          }}>
            <div style={{ fontSize: 12.5, color: "var(--brown-deep)", lineHeight: 1.6,
                          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {q.text}
            </div>
            <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 4, display: "flex", justifyContent: "space-between", gap: 6 }}>
              <span>
                {(q.platforms || []).map((p) => PLATFORM_META[p]?.icon || "•").join(" ")}
                · {q.status === "queued" ? "🕐 في الانتظار" : q.status === "publishing" ? "📤 نشر..." : q.status === "published" ? "✅ تم" : "❌ فشل"}
              </span>
              <span>{relTime(q.scheduled_for || q.created_at)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SETTINGS VIEW ───────────────────────────────────────────────────────